import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { organisationsApi, sarApi, authApi, profileApi } from '../lib/api';
import { Button } from '../components/ui/button';
import AppShell from '../components/AppShell';
import { PageHeader } from '../components/PageHeader';
import MonthYearPicker from '../components/MonthYearPicker';
import SchoolYearPicker from '../components/SchoolYearPicker';
import DateSenseChecks from '../components/DateSenseChecks';
import OrgAutocomplete from '../components/OrgAutocomplete';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { normaliseName } from '../lib/textCase';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Building2, X, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getOrgTypeLabel } from '../lib/utils';
import { OrgLogo } from '../components/OrgLogo';
import { OrgStatusPill } from '../components/StatusPills';
import { CarerNamesInput } from '../components/CarerNamesInput';
import { RespiteCarerPicker } from '../components/RespiteCarerPicker';
import { AddOrganisationWizard } from '../components/organisations/AddOrganisationWizard';
import {
  ORG_TYPES,
  ORG_TYPES_WITH_PLACEMENTS,
  PLACEMENT_TYPES,
  CARER_LED_TYPES,
  BUILDING_LED_TYPES,
  PROFESSIONAL_ROLES,
  HOLDING_ORG_NAME,
} from '../lib/orgTypes';


// Get icon component for org type
const getOrgIcon = (type) => {
  const found = ORG_TYPES.find(t => t.value === type);
  return found?.icon || Building2;
};

// Format date range
const formatDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return null;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const [year, month] = dateStr.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[parseInt(month) - 1]} ${year}`;
    } catch {
      return dateStr;
    }
  };
  
  const start = formatDate(startDate);
  const end = formatDate(endDate) || 'Present';
  
  if (start) {
    return `${start} - ${end}`;
  }
  return end !== 'Present' ? `Until ${end}` : null;
};

export const Organisations = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [organisations, setOrganisations] = useState([]);
  const [respitePool, setRespitePool] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtering and sorting
  const [sortBy, setSortBy] = useState('name');
  const [filterType, setFilterType] = useState('all');
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardInitialType, setWizardInitialType] = useState(null);
  const [editingOrg, setEditingOrg] = useState(null);
  const [deletingOrg, setDeletingOrg] = useState(null);
  // Set once the backend refuses a delete because a request is still in
  // progress — holds how many, and switches the dialog to the "this will
  // withdraw it and email them" confirmation before retrying with
  // confirmActiveSar.
  const [pendingSarWithdrawal, setPendingSarWithdrawal] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [newlyAddedOrg, setNewlyAddedOrg] = useState(null); // For SAR prompt after adding
  
  // Form state
  const [formData, setFormData] = useState({
    type: '',
    name: '',
    location: '',
    address: '',
    email: '',
    website: '',
    start_date: '',
    end_date: '',
    placements: [],
    professionals: [],
    year_group_start: '',
    year_group_end: '',
    notes: '',
    reference_numbers: ''
  });
  const [saving, setSaving] = useState(false);
  const [generatingSar, setGeneratingSar] = useState(null);

  useEffect(() => {
    loadOrganisations();
  }, []);


  // A nested placement row's bin: which placement is waiting for "Remove?".
  const [removingPlacement, setRemovingPlacement] = useState(null);
  const confirmRemovePlacement = async () => {
    const { org, placement } = removingPlacement;
    try {
      await organisationsApi.deletePlacement(org.id, placement.id);
      toast.success('Placement removed.');
      setRemovingPlacement(null);
      loadOrganisations();
    } catch {
      toast.error('It could not be removed.');
    }
  };

  const loadOrganisations = async () => {
    try {
      // Through the api client, which opens the sealed placement and people
      // details (lib/vault.js) — a plain fetch would show them blank.
      const { data } = await organisationsApi.getEnriched();
      setOrganisations(data.organisations || []);
      // Also load the user's respite-carer pool
      try {
        const me = await authApi.getMe();
        setRespitePool(me?.data?.respite_carers || []);
      } catch {
        // Soft-fail — picker will just have an empty pool
      }
    } catch (error) {
      toast.error('Your organisations could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  // Persist respite pool changes back to /profile so newly created carers
  // are available across the user's other placements.
  const persistRespitePool = async (pool) => {
    setRespitePool(pool);
    try {
      await profileApi.update({ respite_carers: pool });
    } catch {
      toast.error('The respite carer could not be saved.');
    }
  };

  // Filtered, sorted, and parent-grouped organisations.
  // Children (orgs with parent_org_id pointing at a visible parent) are
  // emitted immediately after their parent and rendered indented. If the
  // parent is filtered out, the child is shown at root level.
  const filteredOrganisations = useMemo(() => {
    let result = [...organisations];
    
    // Filter by type
    if (filterType !== 'all') {
      result = result.filter(org => org.type === filterType);
    }
    
    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'date_newest':
          return (b.start_date || '').localeCompare(a.start_date || '');
        case 'date_oldest':
          return (a.start_date || '').localeCompare(b.start_date || '');
        default:
          return 0;
      }
    });
    
    // Build parent → children groups using parent_org_id
    const visibleIds = new Set(result.map(o => o.id));
    const childrenByParent = new Map();
    const roots = [];
    for (const org of result) {
      const parentId = org.parent_org_id;
      if (parentId && visibleIds.has(parentId)) {
        if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
        childrenByParent.get(parentId).push(org);
      } else {
        roots.push(org);
      }
    }
    
    // Flatten parent → children, attaching depth for the renderer
    const ordered = [];
    for (const root of roots) {
      ordered.push({ org: root, depth: 0 });
      const kids = childrenByParent.get(root.id) || [];
      for (const k of kids) {
        ordered.push({ org: k, depth: 1, parentName: root.name });
      }
      // Its placements (a foster home, kinship care…) nested under it. A
      // placement that is its own organisation already shows as a row above.
      for (const p of root.placements || []) {
        if (p.id && !p.linked_org_id) ordered.push({ kind: 'placement', placement: p, org: root, depth: 1 });
      }
    }
    return ordered;
  }, [organisations, filterType, sortBy]);


  const resetForm = useCallback(() => {
    setFormData({
      type: '', name: '', location: '', address: '', email: '', website: '',
      start_date: '', end_date: '', placements: [], professionals: [],
      year_group_start: '', year_group_end: '', notes: '', reference_numbers: '',
      canonical_org_id: null
    });
  }, []);

  const openEditDialog = useCallback((org) => {
    setEditingOrg(org);
    setFormData({
      type: org.type || '',
      name: org.name || '',
      location: org.location || '',
      address: org.address || '',
      email: org.email || '',
      website: org.website || '',
      start_date: org.start_date || '',
      end_date: org.end_date || '',
      placements: org.placements || [],
      professionals: org.social_workers || org.professionals || [],
      year_group_start: org.year_group_start || '',
      year_group_end: org.year_group_end || '',
      notes: org.notes || '',
      reference_numbers: org.reference_numbers || '',
      // Preserve nesting metadata — a missing value in the PUT payload would
      // otherwise let the backend silently wipe the child/parent link.
      parent_org_id: org.parent_org_id || null,
      parent_placement_id: org.parent_placement_id || null,
      canonical_org_id: org.canonical_org_id || null,
    });
    setShowAddDialog(true);
  }, []);


  // Adding goes through the guided wizard; the form dialog below is now
  // only for editing an existing organisation (and for the
  // placement-to-organisation hand-off, which pre-fills it).
  const openAddDialog = useCallback((type = null) => {
    setWizardInitialType(type);
    setShowWizard(true);
  }, []);

  const handleWizardAdded = async (result) => {
    const { kind, organisation, addressesAdded = [], holdingReused, childOrganisations = [], placingOrganisation } = result || {};
    if (kind === 'placement') {
      toast.success(`Added under ${organisation?.name || 'that organisation'}.`);
    } else if (kind === 'holding') {
      toast.success(
        `${holdingReused ? 'Added to' : 'Saved under'} \u201c${HOLDING_ORG_NAME}\u201d. We will ask which council when a record names it.`,
        { duration: 6000 }
      );
    } else {
      const extras = [
        childOrganisations.length ? `${childOrganisations.length} home${childOrganisations.length === 1 ? '' : 's'} added as ${childOrganisations.length === 1 ? 'its' : 'their'} own organisation` : null,
        placingOrganisation?.unresolved ? `filed under \u201c${HOLDING_ORG_NAME}\u201d until you know the council` : placingOrganisation ? `linked to ${placingOrganisation.name}` : null,
      ].filter(Boolean);
      toast.success(extras.length ? `Organisation added \u2014 ${extras.join('; ')}.` : 'Organisation added.');
    }
    if (addressesAdded.length > 0) {
      setTimeout(() => {
        toast.info(
          `${addressesAdded.length} address${addressesAdded.length > 1 ? 'es' : ''} added to your profile from the placement details.`,
          { duration: 5000 }
        );
      }, 500);
    }
    await loadOrganisations();
    // Only a real organisation can be asked for records — never a holder,
    // and a placement added under an existing org already has its status.
    if (kind === 'organisation' && organisation && !organisation.unresolved) setNewlyAddedOrg(organisation);
  };

  // Handle ?action=add or ?edit={id} URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'add') {
      // A dashboard nudge that already knows what it's suggesting (e.g.
      // "do you remember your secondary school?") passes ?type= to skip
      // straight past the tile grid.
      openAddDialog(params.get('type'));
      navigate('/organisations', { replace: true });
      return;
    }
    const editId = params.get('edit');
    if (editId && organisations.length > 0) {
      const orgToEdit = organisations.find(o => o.id === editId);
      if (orgToEdit) {
        openEditDialog(orgToEdit);
        navigate('/organisations', { replace: true });
      }
    }
  }, [location.search, organisations, navigate, openAddDialog, openEditDialog]);

  const addPlacement = () => {
    setFormData({
      ...formData,
      placements: [...formData.placements, {
        id: (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `p_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        type: '', name: '', address: '', carer_names: [], start_date: '', end_date: '', notes: ''
      }]
    });
  };

  const updatePlacement = (index, field, value) => {
    const updated = [...formData.placements];
    updated[index][field] = value;
    setFormData({ ...formData, placements: updated });
  };

  const removePlacement = (index) => {
    setFormData({ ...formData, placements: formData.placements.filter((_, i) => i !== index) });
  };

  // Convert a building-led placement (children's home / residential unit /
  // semi-independent) into its own top-level organisation linked back to the
  // current org. The current org's other edits are preserved by saving first.
  const createPlacementAsOrganisation = async (index) => {
    const placement = formData.placements[index];
    if (!placement?.name) {
      toast.error('Add a name for the placement first.');
      return;
    }
    // Map placement type → organisation type
    const orgTypeMap = {
      childrens_home: 'childrens_home',
      residential: 'childrens_home',
      semi_independent: 'childrens_home',
    };
    const newOrgType = orgTypeMap[placement.type] || 'childrens_home';

    // Save the current org first so the placement isn't lost
    setSaving(true);
    try {
      const dataToSend = { ...formData, social_workers: formData.professionals };
      let parentOrgId = editingOrg?.id;
      if (editingOrg) {
        await organisationsApi.update(editingOrg.id, dataToSend);
      } else {
        const response = await organisationsApi.add(dataToSend);
        parentOrgId = response.data?.organisation?.id;
      }
      await loadOrganisations();
      // Now reset and open a fresh dialog pre-filled with the placement details.
      // Pre-populate the new org's professionals with the carers that were
      // recorded against this placement — keeps the people-records joined up.
      const carriedProfessionals = (placement.carer_names || []).map((n) => ({
        name: n,
        role: 'key_worker',
        dates_known: '',
      }));
      setEditingOrg(null);
      setFormData({
        type: newOrgType,
        name: placement.name,
        location: placement.address || '',
        address: placement.address || '',
        email: '',
        website: '',
        start_date: placement.start_date || '',
        end_date: placement.end_date || '',
        placements: [],
        professionals: carriedProfessionals,
        year_group_start: '',
        year_group_end: '',
        notes: parentOrgId
          ? `Created from a placement linked to organisation ${formData.name}.`
          : '',
        reference_numbers: '',
        parent_org_id: parentOrgId,
        parent_placement_id: placement.id,
      });
      toast.success(`Saved. Now add ${placement.name} as its own organisation.`);
    } catch {
      toast.error('The organisation could not be saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const addProfessional = () => {
    const defaultRole = PROFESSIONAL_ROLES[formData.type]?.[0]?.value || 'other';
    setFormData({
      ...formData,
      professionals: [...formData.professionals, { name: '', role: defaultRole, dates_known: '' }]
    });
  };

  const updateProfessional = (index, field, value) => {
    const updated = [...formData.professionals];
    updated[index][field] = value;
    setFormData({ ...formData, professionals: updated });
  };

  const removeProfessional = (index) => {
    setFormData({ ...formData, professionals: formData.professionals.filter((_, i) => i !== index) });
  };

  const handleSaveOrganisation = async () => {
    if (!formData.type || !formData.name) {
      toast.error('Choose a type and enter a name first.');
      return;
    }

    setSaving(true);
    try {
      const dataToSend = { ...formData, social_workers: formData.professionals };
      
      if (editingOrg) {
        await organisationsApi.update(editingOrg.id, dataToSend);
        toast.success('Saved.');
        await loadOrganisations();
        setShowAddDialog(false);
        resetForm();
        setEditingOrg(null);
      } else {
        const response = await organisationsApi.add(dataToSend);
        const addedOrg = response.data?.organisation;
        const addressesAdded = response.data?.addresses_auto_added || [];
        
        toast.success('Organisation added.');
        
        // Show toast for auto-added addresses
        if (addressesAdded.length > 0) {
          setTimeout(() => {
            toast.info(
              `${addressesAdded.length} address${addressesAdded.length > 1 ? 'es' : ''} added to your profile from the placement details.`,
              { duration: 5000 }
            );
          }, 500);
        }
        
        await loadOrganisations();
        setShowAddDialog(false);
        resetForm();
        // Show SAR creation prompt for newly added org
        if (addedOrg) {
          setNewlyAddedOrg(addedOrg);
        }
      }
    } catch (error) {
      toast.error('The organisation could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadZip = async (org) => {
    setDownloadingZip(true);
    try {
      const response = await organisationsApi.downloadDocumentsZip(org.id);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${org.name.replace(/\s+/g, '_')}_documents.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Documents downloaded.');
    } catch (error) {
      if (error.message?.includes('Encryption key not found')) {
        toast.error('Your session has expired. Sign out and back in to download encrypted files.', {
          duration: 5000,
        });
      } else {
        toast.error(error.response?.data?.detail || error.message || 'The documents could not be downloaded.');
      }
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleDeleteOrganisation = async () => {
    if (!deletingOrg) return;
    setDeleting(true);
    try {
      await organisationsApi.delete(deletingOrg.id, { confirmActiveSar: !!pendingSarWithdrawal });
      toast.success(
        pendingSarWithdrawal ? 'Organisation deleted and the request withdrawn.' : 'Organisation deleted.',
      );
      await loadOrganisations();
      setDeletingOrg(null);
      setPendingSarWithdrawal(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.requires_confirmation) {
        // Not an error the user needs a toast for — the dialog itself
        // switches to ask for this specific confirmation.
        setPendingSarWithdrawal({ count: detail.in_progress_sar_count || 1 });
      } else {
        toast.error((typeof detail === 'string' && detail) || 'The organisation could not be deleted.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateSar = async (org) => {
    setGeneratingSar(org.id);
    try {
      const res = await sarApi.generate({
        organisation_id: org.id,
        organisation_name: org.name,
      });
      toast.success('Request created.');
      navigate(`/sar/${res.data.id}`);
    } catch (error) {
      toast.error('The request could not be created.');
    } finally {
      setGeneratingSar(null);
    }
  };


  const showPlacementFields = ORG_TYPES_WITH_PLACEMENTS.includes(formData.type);
  const availableRoles = PROFESSIONAL_ROLES[formData.type] || PROFESSIONAL_ROLES.council;

  const getProfessionalLabel = (type) => {
    const labels = {
      council: 'Social Workers & Professionals',
      childrens_home: 'Staff Members',
      adoption: 'Adoption Workers',
      school: 'Teachers & Staff',
      doctor: 'Medical Staff',
      hospital: 'Medical Staff',
      police: 'Police Officers',
      mental_health: 'Mental Health Workers',
    };
    return labels[type] || 'Professionals';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ink-55" aria-label="Loading" />
      </div>
    );
  }

  return (
    <AppShell>

      <main>
        <PageHeader
          title="Organisations"
          lede={organisations.length === 0
            ? 'Every council, school and service that kept a file on you.'
            : `You are tracking ${organisations.length} organisation${organisations.length !== 1 ? 's' : ''}.`}
          actions={(
            <Button onClick={() => openAddDialog()} data-testid="add-org-btn">
              Add an organisation
            </Button>
          )}
        />

        {/* Educational hint — surfaces only when the user has fewer than
            three orgs, so it nudges them to broaden the search but doesn't
            keep nagging once they've clearly understood the value. */}
        {organisations.length < 3 && (
          <div className="mb-6 p-5 rounded-lg bg-ground" data-testid="orgs-hint-card">
            <div className="text-[15px] text-ink-70 leading-relaxed space-y-2 max-w-[68ch]">
              <p className="font-semibold text-ink">Add every organisation that knew about your care.</p>
              <p>
                Schools, GPs, hospitals and even the police often received
                copies of reports, attended meetings or kept their own notes
                about you. Each one holds a different part of your records.
              </p>
              <p>
                It is also the best way to see past redactions. When one
                organisation blacks out a paragraph, another that received the
                same report may not have.
              </p>
            </div>
          </div>
        )}

        {/* Filters and Sort */}
        {organisations.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-ground rounded-lg">
            <span className="pd-eyebrow">Filter</span>
            <div className="flex items-center gap-2">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[190px] bg-surface border-surface" aria-label="Filter by type">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {ORG_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[190px] bg-surface border-surface" aria-label="Sort by">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name, A to Z</SelectItem>
                  <SelectItem value="date_newest">Newest first</SelectItem>
                  <SelectItem value="date_oldest">Oldest first</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Organisation Cards */}
        {filteredOrganisations.length === 0 ? (
          <div className="rounded-lg bg-ground py-14 px-6 text-center">
            <div aria-hidden="true" className="w-[52px] h-[52px] rounded-lg bg-inset mx-auto mb-5" />
            <h3 className="font-heading text-[26px] font-semibold tracking-[-0.02em] text-ink mb-2">
              {organisations.length === 0 ? 'Nothing here yet' : 'Nothing matches this filter'}
            </h3>
            <p className="text-base text-ink-55 mb-6 max-w-md mx-auto">
              {organisations.length === 0
                ? 'Organisations appear here once you add them. Start with the council that was responsible for you.'
                : 'Choose a different type to see more organisations.'}
            </p>
            {organisations.length === 0 ? (
              <Button variant="secondary" onClick={() => openAddDialog()}>
                Add an organisation
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => setFilterType('all')}>
                Show all types
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-3">
            {filteredOrganisations.map((item) => {
              if (item.kind === 'placement') {
                const p = item.placement;
                const typeLabel = PLACEMENT_TYPES.find((t) => t.value === p.type)?.label;
                return (
                  <div key={`pl-${p.id}`} className="pl-6 sm:pl-10 relative" data-testid={`placement-row-${p.id}`}>
                    <span aria-hidden className="absolute left-2 sm:left-4 top-0 bottom-1/2 w-0.5 bg-rule rounded-full" />
                    <span aria-hidden className="absolute left-2 sm:left-4 top-1/2 w-3 sm:w-5 h-0.5 bg-rule rounded-full" />
                    <Card className="bg-inset" data-testid={`placement-card-${p.id}`}>
                      <CardContent className="p-4 sm:p-5">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink truncate">
                                {p.name || (p.carer_names?.length ? p.carer_names.join(' and ') : 'A placement')}
                              </h3>
                              <span className="text-[13px] px-[10px] py-[3px] rounded-full bg-surface text-ink-70">via {item.org.name}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-sm text-ink-55 flex-wrap">
                              {typeLabel && <span>{typeLabel}</span>}
                              {typeLabel && (p.start_date || p.end_date) && <span aria-hidden="true">·</span>}
                              {(p.start_date || p.end_date) && (
                                <span className="pd-mono">{formatDateRange(p.start_date, p.end_date) || ''}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              className="bg-surface hover:bg-inset"
                              onClick={() => navigate(`/organisations/${item.org.id}/placements/${p.id}`)}
                              data-testid={`placement-row-open-${p.id}`}
                            >
                              View
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => setRemovingPlacement({ org: item.org, placement: p })}
                              className="h-9 w-9 hover:text-breach-ink"
                              aria-label={`Remove placement ${p.name || (p.carer_names || []).join(' and ') || ''}`.trim()}
                              data-testid={`placement-row-delete-${p.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              }
              const { org, depth = 0, parentName } = item;
              const OrgIcon = getOrgIcon(org.type);
              const dateRange = formatDateRange(org.start_date, org.end_date);
              const isChild = depth > 0;
              
              return (
                <div
                  key={org.id}
                  className={isChild ? 'pl-6 sm:pl-10 relative' : ''}
                  data-testid={`org-row-${org.id}`}
                >
                  {isChild && (
                    <span
                      aria-hidden
                      className="absolute left-2 sm:left-4 top-0 bottom-1/2 w-0.5 bg-rule rounded-full"
                    />
                  )}
                  {isChild && (
                    <span
                      aria-hidden
                      className="absolute left-2 sm:left-4 top-1/2 w-3 sm:w-5 h-0.5 bg-rule rounded-full"
                    />
                  )}
                  <Card
                    className={isChild ? 'bg-inset' : 'bg-ground'}
                    data-testid={`org-card-${org.id}`}
                  >
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        {/* Avatar & Info */}
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <OrgLogo
                            orgId={org.id}
                            website={org.website}
                            FallbackIcon={OrgIcon}
                            size={isChild ? 40 : 48}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink truncate">{org.name}</h3>
                              {org.has_news && (
                                <span
                                  className="inline-flex items-center gap-1.5 text-[13px] text-brand-ink"
                                  title="Something new to look at since you last opened it"
                                  data-testid={`org-news-${org.id}`}
                                >
                                  <span aria-hidden="true" className="w-[8px] h-[8px] rounded-full bg-brand" />
                                  New
                                </span>
                              )}
                              <OrgStatusPill status={org.org_status} reason={org.org_status_reason} daysRemaining={org.days_remaining} size="sm" />
                              {isChild && parentName && (
                                <span
                                  className="text-[13px] px-[10px] py-[3px] rounded-full bg-surface text-ink-70"
                                  title={`Linked to ${parentName}`}
                                  data-testid={`linked-badge-${org.id}`}
                                >
                                  via {parentName}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1 text-sm text-ink-55 flex-wrap">
                              <span>{org.unresolved && org.location ? `Placements in ${org.location}` : getOrgTypeLabel(org.type)}</span>
                              {dateRange && (
                                <>
                                  <span aria-hidden="true">·</span>
                                  <span className="pd-mono">{dateRange}</span>
                                </>
                              )}
                              {org.document_count > 0 && (
                                <>
                                  <span aria-hidden="true">·</span>
                                  <span>{org.document_count} document{org.document_count !== 1 ? 's' : ''}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                        <Button
                          size="sm"
                          variant="secondary"
                          className="bg-surface hover:bg-inset"
                          onClick={() => navigate(`/organisations/${org.id}`)}
                          data-testid={`view-org-${org.id}`}
                        >
                          View
                        </Button>

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(org)}
                          data-testid={`edit-org-${org.id}`}
                        >
                          Edit
                        </Button>

                        {org.sar_id ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/sar/${org.sar_id}`)}
                            data-testid={`view-sar-${org.id}`}
                          >
                            View request
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleCreateSar(org)}
                            disabled={generatingSar === org.id}
                            data-testid={`create-sar-${org.id}`}
                          >
                            {generatingSar === org.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" aria-label="Creating request" />
                            ) : (
                              'Create request'
                            )}
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/documents?org=${org.id}`)}
                          data-testid={`view-docs-${org.id}`}
                        >
                          Documents
                        </Button>

                        <Button
                          size="sm"
                          variant="ghost"
                          className="hover:text-breach-ink"
                          onClick={() => { setDeletingOrg(org); setPendingSarWithdrawal(null); }}
                          data-testid={`delete-org-${org.id}`}
                          aria-label={`Delete ${org.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!removingPlacement} onOpenChange={(o) => !o && setRemovingPlacement(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this placement?</AlertDialogTitle>
            <AlertDialogDescription>
              {removingPlacement && `${removingPlacement.placement.name || (removingPlacement.placement.carer_names || []).join(' and ') || 'This placement'} will be removed from ${removingPlacement.org.name}, with your notes about it. Records and documents aren’t affected.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemovePlacement} data-testid="placement-row-delete-confirm">Remove placement</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deletingOrg} onOpenChange={(open) => { if (!open) { setDeletingOrg(null); setPendingSarWithdrawal(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">
              Delete {deletingOrg?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-ink-70">
              <p>This permanently removes:</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                <li>The organisation and all its details</li>
                <li>Every request you sent them, including any follow-ups</li>
                {deletingOrg?.document_count > 0 && (
                  <li className="text-breach-ink font-medium">
                    {deletingOrg.document_count} document{deletingOrg.document_count !== 1 ? 's' : ''} from this organisation
                  </li>
                )}
              </ul>

              {deletingOrg?.document_count > 0 && (
                <div className="mt-4 p-3 bg-amber-wash rounded-lg space-y-2">
                  <p className="text-sm text-amber-ink font-medium">
                    Download your documents first. They cannot be recovered after this.
                  </p>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="bg-surface"
                    onClick={() => handleDownloadZip(deletingOrg)}
                    disabled={downloadingZip}
                  >
                    {downloadingZip && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Download all documents (ZIP)
                  </Button>
                </div>
              )}

              {pendingSarWithdrawal && (
                <div className="mt-4 p-3 bg-breach-wash rounded-lg">
                  <p className="text-sm text-breach-ink font-medium">
                    {pendingSarWithdrawal.count === 1
                      ? 'A request to this organisation is still in progress.'
                      : `${pendingSarWithdrawal.count} requests to this organisation are still in progress.`}
                    {' '}Deleting anyway will email {deletingOrg?.name || 'them'} to say the
                    {pendingSarWithdrawal.count === 1 ? ' request is' : ' requests are'} withdrawn, then delete everything.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteOrganisation(); }}
              disabled={deleting}
              className="bg-breach-wash text-breach hover:bg-[#F6D2CA]"
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {pendingSarWithdrawal ? 'Withdraw and delete' : 'Delete organisation'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* SAR Creation Prompt Dialog - shown after adding a new org */}
      <AlertDialog open={!!newlyAddedOrg} onOpenChange={() => setNewlyAddedOrg(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">
              Ask {newlyAddedOrg?.name} for your records now?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-ink-70">
              <p>{newlyAddedOrg?.name} has been added to your organisations.</p>
              <p>
                You can create a request to them now. This is a Subject Access Request,
                which is your legal right to see what they hold about you. Once it is sent,
                they have 30 days to reply.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not now</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const org = newlyAddedOrg;
                setNewlyAddedOrg(null);
                handleCreateSar(org);
              }}
              disabled={generatingSar}
            >
              {generatingSar && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Create request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Guided add flow */}
      <AddOrganisationWizard
        open={showWizard}
        onOpenChange={(open) => { setShowWizard(open); if (!open) setWizardInitialType(null); }}
        initialType={wizardInitialType}
        dob={user?.date_of_birth}
        organisations={organisations}
        respitePool={respitePool}
        onRespitePoolChange={persistRespitePool}
        onAdded={handleWizardAdded}
      />

      {/* Edit Organisation Dialog (also used after a placement is promoted to its own organisation) */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) { setEditingOrg(null); resetForm(); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">
              {editingOrg ? 'Edit organisation' : 'Add an organisation'}
            </DialogTitle>
            <DialogDescription className="text-ink-55">
              Add as much as you remember. Names of staff help organisations find your records. Rough dates are fine.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            {/* Basic Info */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[15px] font-semibold">What kind of organisation is it?</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({...formData, type: v, professionals: []})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ORG_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[15px] font-semibold">What is it called?</Label>
                <OrgAutocomplete
                  value={formData.name}
                  type={formData.type}
                  onChange={(v) => setFormData({...formData, name: v})}
                  onBlur={(e) => setFormData({...formData, name: normaliseName(e.target.value) || e.target.value})}
                  onPick={(picked) => {
                    setFormData((prev) => ({
                      ...prev,
                      name: picked.name,
                      email: picked.privacy_email || prev.email,
                      address: picked.address || prev.address,
                      website: picked.website || prev.website,
                      canonical_org_id: picked.id,
                    }));
                  }}
                  placeholder="For example, Birmingham City Council"
                  testId="org-form-name-autocomplete"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[15px] font-semibold">Which town or city?</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  onBlur={(e) => setFormData({...formData, location: normaliseName(e.target.value) || e.target.value})}
                  placeholder="For example, Birmingham"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[15px] font-semibold">Do you know their email address?</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="data.protection@council.gov.uk"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[15px] font-semibold">Do you know their website?</Label>
              <p className="text-sm text-ink-55">
                Used to show their logo. If you leave it blank we try the email address instead.
              </p>
              <Input
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                placeholder="For example, birmingham.gov.uk"
                data-testid="org-website-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[15px] font-semibold">Do you know their postal address?</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Full postal address"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[15px] font-semibold">When did your time with them start?</Label>
                <p className="text-sm text-ink-55">A rough month and year is fine.</p>
                <MonthYearPicker
                  value={formData.start_date}
                  onChange={(v) => setFormData({...formData, start_date: v})}
                  testId="org-start-date"
                  ariaLabel="Organisation start date"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[15px] font-semibold">When did it end?</Label>
                <p className="text-sm text-ink-55">Leave blank if it is still going.</p>
                <MonthYearPicker
                  value={formData.end_date}
                  onChange={(v) => setFormData({...formData, end_date: v})}
                  testId="org-end-date"
                  ariaLabel="Organisation end date"
                />
              </div>
            </div>

            {/* Sense-check warnings against DOB / now */}
            <DateSenseChecks
              dob={user?.date_of_birth}
              startDate={formData.start_date}
              endDate={formData.end_date}
              type={formData.type}
              testIdPrefix="org-form-date-check"
            />

            {/* School-specific: year-group picker (auto-fills dates) */}
            {formData.type === 'school' && (
              <SchoolYearPicker
                dob={user?.date_of_birth}
                yearGroupStart={formData.year_group_start || ''}
                yearGroupEnd={formData.year_group_end || ''}
                startDate={formData.start_date || ''}
                endDate={formData.end_date || ''}
                testIdPrefix="org-form-school-yp"
                onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
              />
            )}

            {/* Placements */}
            {showPlacementFields && (
              <div className="space-y-4 pt-6 border-t border-rule">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-heading text-[19px] font-semibold tracking-[-0.02em]">Where did they place you?</h4>
                    <p className="text-sm text-ink-55">Foster homes, children's homes and similar.</p>
                  </div>
                  <Button type="button" variant="secondary" size="sm" onClick={addPlacement}>
                    Add a placement
                  </Button>
                </div>
                
                {formData.placements.map((placement, index) => {
                  const isCarerLed = CARER_LED_TYPES.has(placement.type);
                  const isBuildingLed = BUILDING_LED_TYPES.has(placement.type);
                  const namePlaceholder = isCarerLed
                    ? 'A name you know them by, for example The Smiths'
                    : isBuildingLed
                    ? 'Name of the home or unit'
                    : 'Name of placement';
                  const peopleLabel = isCarerLed
                    ? 'Who looked after you there?'
                    : isBuildingLed
                    ? 'Who worked with you there?'
                    : null;
                  const peopleHint = isCarerLed
                    ? 'Foster carers, kinship carers or hosts.'
                    : 'Key workers, residential staff or carers.';
                  return (
                  <div key={index} className="p-4 bg-ground rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="pd-eyebrow">Placement {index + 1}</span>
                      <Button variant="ghost" size="icon" onClick={() => removePlacement(index)} className="h-9 w-9" aria-label={`Remove placement ${index + 1}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Select value={placement.type} onValueChange={(v) => updatePlacement(index, 'type', v)}>
                        <SelectTrigger className="bg-surface border-surface" aria-label="Placement type">
                          <SelectValue placeholder="Placement type" />
                        </SelectTrigger>
                        <SelectContent>
                          {PLACEMENT_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={placement.name || ''}
                        onChange={(e) => updatePlacement(index, 'name', e.target.value)}
                        onBlur={(e) => updatePlacement(index, 'name', normaliseName(e.target.value) || e.target.value)}
                        placeholder={namePlaceholder}
                        className="bg-surface border-surface"
                        aria-label="Placement name"
                      />
                    </div>

                    {/* People chip input — both carer-led and building-led placements */}
                    {peopleLabel && (
                      <div>
                        <Label className="text-[15px] font-semibold mb-1 block">
                          {peopleLabel}
                        </Label>
                        <p className="text-sm text-ink-55 mb-1.5">{peopleHint}</p>
                        <CarerNamesInput
                          value={placement.carer_names || []}
                          onChange={(arr) => updatePlacement(index, 'carer_names', arr)}
                          placeholder="Type a name and press Enter"
                          testId={`placement-${index}-carers`}
                        />
                      </div>
                    )}

                    {/* Third-party advice + create-as-org CTA — building-led placements.
                        Hide once the placement has already been promoted to its
                        own organisation (detected via parent_placement_id
                        backlink, with a fallback to name+parent match for
                        legacy placements that pre-date the placement id). */}
                    {isBuildingLed && (() => {
                      const linkedOrg = organisations.find(
                        (o) =>
                          (placement.id && o.parent_placement_id === placement.id) ||
                          (editingOrg?.id &&
                            o.parent_org_id === editingOrg.id &&
                            (o.name || '').trim().toLowerCase() ===
                              (placement.name || '').trim().toLowerCase())
                      );
                      if (linkedOrg) {
                        return (
                          <p
                            className="p-3 rounded-md bg-inset text-sm text-ink-70 leading-relaxed"
                            data-testid={`placement-${index}-linked-org-info`}
                          >
                            Already added as its own organisation:{' '}
                            <strong className="text-ink">{linkedOrg.name}</strong>. Manage any
                            request for this placement on that organisation's page.
                          </p>
                        );
                      }
                      return (
                        <div
                          className="p-3 rounded-lg bg-amber-wash space-y-2"
                          data-testid={`placement-${index}-third-party-advice`}
                        >
                          <p className="text-sm text-amber-ink leading-relaxed">
                            Children's homes, residential units and semi-independent
                            providers are often run by a separate company. If this
                            placement was run by a different organisation from the one
                            above, add it as its own organisation so you can send it a
                            separate request. They keep their own records. Names you
                            added above are carried over.
                          </p>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => createPlacementAsOrganisation(index)}
                            disabled={!placement.name || saving}
                            className="bg-surface"
                            data-testid={`placement-${index}-create-as-org`}
                          >
                            Add as a separate organisation
                          </Button>
                          {!placement.name && (
                            <p className="text-sm text-amber-ink">
                              Add a name above first.
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-[15px] font-semibold">When did you move in?</Label>
                        <MonthYearPicker
                          value={placement.start_date || ''}
                          onChange={(v) => updatePlacement(index, 'start_date', v)}
                          ariaLabel="Placement start date"
                          className="[&_button]:bg-surface [&_button]:border-surface"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[15px] font-semibold">When did you leave?</Label>
                        <MonthYearPicker
                          value={placement.end_date || ''}
                          onChange={(v) => updatePlacement(index, 'end_date', v)}
                          ariaLabel="Placement end date"
                          className="[&_button]:bg-surface [&_button]:border-surface"
                        />
                      </div>
                    </div>

                    {/* Respite carers — separate from main carers, shared
                        across this user's placements via a known-pool.
                        Placed after the dates so users don't read the
                        date range as relating to respite stays. */}
                    {placement.type && placement.type !== '' && (
                      <div>
                        <Label className="text-[15px] font-semibold mb-1.5 block">
                          Did anyone else look after you for short breaks?
                        </Label>
                        <RespiteCarerPicker
                          attached={placement.respite || (placement.respite_carer_ids || []).map((id) => ({ carer_id: id, pattern: null }))}
                          pool={respitePool}
                          onChange={({ attached, pool }) => {
                            updatePlacement(index, 'respite', attached);
                            // Persist any newly-added carers back to the user
                            // profile so other placements can pick them up.
                            if (pool.length !== respitePool.length) {
                              persistRespitePool(pool);
                            }
                          }}
                          testId={`placement-${index}-respite`}
                        />
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}

            {/* Professionals */}
            {formData.type && (
              <div className="space-y-4 pt-6 border-t border-rule">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-heading text-[19px] font-semibold tracking-[-0.02em]">{getProfessionalLabel(formData.type)}</h4>
                    <p className="text-sm text-ink-55">Names help them find your records.</p>
                  </div>
                  <Button type="button" variant="secondary" size="sm" onClick={addProfessional}>
                    Add a person
                  </Button>
                </div>

                {formData.professionals.map((prof, index) => (
                  <div key={index} className="p-3 bg-ground rounded-lg flex items-center gap-3">
                    <Input
                      value={prof.name}
                      onChange={(e) => updateProfessional(index, 'name', e.target.value)}
                      placeholder="Name"
                      className="flex-1 bg-surface border-surface"
                      aria-label="Name"
                    />
                    <Select value={prof.role} onValueChange={(v) => updateProfessional(index, 'role', v)}>
                      <SelectTrigger className="w-48 bg-surface border-surface" aria-label="Role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRoles.map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={() => removeProfessional(index)} className="h-10 w-10" aria-label="Remove this person">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Reference numbers and notes */}
            <div className="space-y-2">
              <Label className="text-[15px] font-semibold">Do you know any reference numbers?</Label>
              <p className="text-sm text-ink-55">Case numbers, NHS numbers and similar.</p>
              <Input
                value={formData.reference_numbers}
                onChange={(e) => setFormData({...formData, reference_numbers: e.target.value})}
                placeholder="Reference numbers"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[15px] font-semibold">Anything else that might help?</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any other information"
                className="min-h-[80px]"
              />
            </div>

            <DialogFooter className="gap-3 pt-4">
              <Button variant="ghost" onClick={() => { setShowAddDialog(false); setEditingOrg(null); resetForm(); }}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveOrganisation}
                disabled={saving || !formData.type || !formData.name}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Saving" /> : (editingOrg ? 'Save' : 'Add organisation')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
};

export default Organisations;
